import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EMAIL_OBS_EVENTS } from '@/mocks/email-observability';
import { formatDate, formatTime } from '@/shared/lib/format';
import { Badge } from '@/shared/ui/badge';
import { Card } from '@/shared/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import {
  EMAIL_OBS_KIND_LABEL,
  EMAIL_OBS_STATE_META,
  filterEmailObsEvents,
  type EmailObsPeriod,
  type EmailObsState,
  type ObsChannel,
} from '../model/email-events';

/** Eventos de e-mail e SMS por campanha. Mesmo desenho da aba Integrações: contadores, filtros, tabela. */
export function EmailsObservabilityTab() {
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState<EmailObsPeriod>('7');
  const [companyId, setCompanyId] = useState('all');
  const [campaignId, setCampaignId] = useState('all');
  const [state, setState] = useState<EmailObsState | 'all'>('all');
  const [channel, setChannel] = useState<ObsChannel | 'all'>('all');
  const openId = params.get('evento');

  const companies = useMemo(() => {
    const map = new Map<string, string>();
    for (const event of EMAIL_OBS_EVENTS) {
      if (channel !== 'all' && (event.channel ?? 'email') !== channel) continue;
      map.set(event.companyId, event.companyName);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [channel]);

  const campaignOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const event of EMAIL_OBS_EVENTS) {
      if (channel !== 'all' && (event.channel ?? 'email') !== channel) continue;
      if (companyId !== 'all' && event.companyId !== companyId) continue;
      map.set(event.campaignId, event.campaignName);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [channel, companyId]);

  useEffect(() => {
    if (campaignId === 'all') return;
    if (!campaignOptions.some((item) => item.id === campaignId)) setCampaignId('all');
  }, [campaignId, campaignOptions]);

  const scoped = useMemo(
    () => filterEmailObsEvents(EMAIL_OBS_EVENTS, { period, companyId, campaignId, state: 'all', channel }),
    [period, companyId, campaignId, channel],
  );
  const counts = useMemo(
    () => ({
      success: scoped.filter((event) => event.state === 'success').length,
      failed: scoped.filter((event) => event.state === 'failed').length,
      pending: scoped.filter((event) => event.state === 'pending').length,
    }),
    [scoped],
  );
  const rows = state === 'all' ? scoped : scoped.filter((event) => event.state === state);
  const selected = EMAIL_OBS_EVENTS.find((event) => event.id === openId) ?? null;

  const setOpenId = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('evento', id);
    else next.delete('evento');
    setParams(next, { replace: true });
  };

  return (
    <>
      <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
        {(
          [
            { key: 'success', label: 'Sucesso', value: counts.success },
            { key: 'failed', label: 'Falha', value: counts.failed },
            { key: 'pending', label: 'Pendente', value: counts.pending },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setState((current) => (current === item.key ? 'all' : item.key))}
            className="text-left"
          >
            <Card className={`p-3 sm:p-4 ${state === item.key ? 'ring-2 ring-primary' : ''}`}>
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p
                className={`mt-1 text-xl font-semibold tabular-nums sm:text-2xl ${
                  item.key === 'failed' && item.value > 0 ? 'text-destructive' : ''
                }`}
              >
                {item.value.toLocaleString('pt-BR')}
              </p>
            </Card>
          </button>
        ))}
      </div>

      <div className="mb-4 grid gap-2 sm:flex sm:flex-wrap sm:items-center">
        <Select value={channel} onValueChange={(value) => setChannel(value as ObsChannel | 'all')}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[9rem]">
            <SelectValue placeholder="Canal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os canais</SelectItem>
            <SelectItem value="email">E-mail</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[11rem]">
            <SelectValue placeholder="Empresa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as empresas</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[12rem]">
            <SelectValue placeholder="Campanha" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as campanhas</SelectItem>
            {campaignOptions.map((campaign) => (
              <SelectItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={(value) => setPeriod(value as EmailObsPeriod)}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[10rem]">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 dias</SelectItem>
            <SelectItem value="30">Últimos 30 dias</SelectItem>
            <SelectItem value="ciclo">Ciclo atual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={(value) => setState(value as EmailObsState | 'all')}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[10rem]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="failed">Falha</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground sm:ml-auto">Dados de demonstração</span>
      </div>

      <div className="glass overflow-hidden rounded-2xl">
        <div className="hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="whitespace-nowrap px-4 py-3 font-medium">Quando</th>
                <th className="px-4 py-3 font-medium">Empresa</th>
                <th className="px-4 py-3 font-medium">Campanha</th>
                <th className="px-4 py-3 font-medium">Canal</th>
                <th className="px-4 py-3 font-medium">Evento</th>
                <th className="px-4 py-3 font-medium">Destinatário</th>
                <th className="px-4 py-3 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Nenhum evento no filtro selecionado.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const meta = EMAIL_OBS_STATE_META[row.state];
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setOpenId(row.id)}
                      className="cursor-pointer border-b border-border/40 transition-colors last:border-0 hover:bg-muted/40"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(row.at)} {formatTime(row.at)}
                      </td>
                      <td className="px-4 py-3">{row.companyName}</td>
                      <td className="max-w-[16rem] truncate px-4 py-3">{row.campaignName}</td>
                      <td className="px-4 py-3">{row.channel === 'sms' ? 'SMS' : 'E-mail'}</td>
                      <td className="px-4 py-3">{EMAIL_OBS_KIND_LABEL[row.kind]}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.recipientMasked}</td>
                      <td className="px-4 py-3">
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden">
          {rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">Nenhum evento no filtro selecionado.</p>
          ) : (
            <ul>
              {rows.map((row) => {
                const meta = EMAIL_OBS_STATE_META[row.state];
                return (
                  <li key={row.id} className="border-b border-border/40 last:border-0">
                    <button
                      type="button"
                      onClick={() => setOpenId(row.id)}
                      className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="font-medium">
                        {EMAIL_OBS_KIND_LABEL[row.kind]} · {row.campaignName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {row.companyName} · {row.recipientMasked}
                      </span>
                      <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>
                          {formatDate(row.at)} {formatTime(row.at)}
                        </span>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <Sheet open={Boolean(selected)} onOpenChange={(open) => !open && setOpenId(null)}>
        <SheetContent className="w-full max-w-none sm:w-[min(40rem,calc(100%-2rem))]">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle>{EMAIL_OBS_KIND_LABEL[selected.kind]}</SheetTitle>
                <SheetDescription>
                  {selected.companyName} · {selected.campaignName} · {selected.recipientMasked}
                </SheetDescription>
              </SheetHeader>
              {selected.channel === 'sms' ? (
                <p className="mb-4 text-sm text-muted-foreground">SMS · texto não exibido.</p>
              ) : (
                <p className="mb-4 text-sm">Assunto: {selected.subject}</p>
              )}
              {selected.cause ? (
                <p className="mb-4 rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm" role="status">
                  Causa: {selected.cause}
                </p>
              ) : null}
              <ol className="space-y-3">
                {selected.sequence.map((step) => (
                  <li key={`${step.at}-${step.label}`} className="rounded-xl border border-border/60 px-3 py-2">
                    <p className="text-sm font-medium">{step.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(step.at)} {formatTime(step.at)}
                    </p>
                    <p className="mt-1 text-sm">{step.detail}</p>
                  </li>
                ))}
              </ol>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
